import { Alert, Button, DialogActions, DialogContent, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/system';
import { useState } from 'react';
import { dsnPageTestId } from './test-ids';

type Props = {
  close: () => void;
  confirm: (reason: string) => void;
  patientName?: string;
  dateOfService?: string;
  therapistName?: string;
  facility?: string;
  loading?: boolean;
  errorMessage?: string;
};

/**
 * BOPS-151 — Archive confirmation.
 *
 * Deliberately near-identical to DeleteNoteConfirmation: same title shape, same four-part
 * identity line, same "This cannot be undone" sentence, same reason field and placeholder,
 * same audit line, same disabled primary. No icon, because Delete has none. The only
 * addition is the second line about the PDF — the one thing that makes archiving different
 * from deleting.
 */
const ArchiveNoteConfirmation = (props: Props) => {
  const {
    close,
    confirm,
    patientName,
    dateOfService,
    therapistName,
    facility,
    loading,
    errorMessage,
  } = props;
  const [reason, setReason] = useState('');

  // Auto-filled context line from the document: Patient | Date of service | Therapist | Facility.
  const contextLine = [patientName, dateOfService, therapistName, facility]
    .filter((part) => !!part && `${part}`.trim())
    .join(' | ');

  return (
    <Box p={1}>
      <DialogContent>
        <Typography
          variant="h5"
          fontWeight={700}
          id="archive-note-dialog-title"
        >
          Archive this Daily Session Note?
        </Typography>

        {contextLine && (
          <Typography
            variant="body2"
            sx={{ mt: 0.5 }}
          >
            {contextLine}
          </Typography>
        )}

        <Typography
          variant="body2"
          sx={{ color: '#111927', mt: 2 }}
        >
          This will permanently archive the note and remove it from billing.{' '}
          <Typography
            component="span"
            variant="body2"
            fontWeight={700}
          >
            This cannot be undone
          </Typography>{' '}
          — the note cannot be returned to billing once archived.
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: '#111927', mt: 2 }}
        >
          The signed PDF stays on the patient&apos;s medical record and can be viewed from Archived
          Documents.
        </Typography>

        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: '#111927', mt: 2, mb: 1 }}
        >
          Why are you archiving this note? (required)
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          inputProps={{ 'data-testid': dsnPageTestId('archive-reason') }}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., documented on the wrong patient, incorrect date of service…"
          sx={{
            '& .MuiInputBase-root': {
              padding: '10px !important',
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{ fontStyle: 'italic', mt: 1, display: 'block' }}
        >
          Your name and reason are recorded for auditing.
        </Typography>

        {/*  The backend refuses an archive on a note with no billable codes, and on one that
            was never submitted so there is no PDF to keep. Both are expected answers rather
            than faults, so they are shown in the dialog the user is already looking at
            instead of as a raw error. The dialog stays open — nothing was archived. */}
        {errorMessage && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
          >
            {errorMessage}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          data-testid={dsnPageTestId('archive-cancel')}
          sx={{ color: '#111927' }}
          onClick={close}
          disabled={loading}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          disabled={!reason.trim() || loading}
          sx={{
            background: '#F04438',
            color: '#fff',
            '&:hover': { background: '#F04438' },
          }}
          onClick={() => confirm(reason.trim())}
          data-testid={dsnPageTestId('archive-confirm')}
        >
          Archive
        </LoadingButton>
      </DialogActions>
    </Box>
  );
};

export default ArchiveNoteConfirmation;
